import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { FileText, Plus, Video, BookOpen, Search } from 'lucide-react';

const contentTypeIcons = {
  article: FileText,
  video: Video,
  guide: BookOpen
};

export default function AdminContent() {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '',
    content_type: 'article',
    body: '',
    category: '',
    tags: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const tags = createForm.tags.split(',').map(t => t.trim()).filter(Boolean);
      await axios.post(
        `${API}/content?title=${encodeURIComponent(createForm.title)}&content_type=${createForm.content_type}&body=${encodeURIComponent(createForm.body)}&category=${encodeURIComponent(createForm.category)}&tags=${encodeURIComponent(JSON.stringify(tags))}`
      );
      toast.success('Content created');
      setShowCreateDialog(false);
      setCreateForm({ title: '', content_type: 'article', body: '', category: '', tags: '' });
      fetchContent();
    } catch (error) {
      toast.error('Failed to create content');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredContent = content.filter(c =>
    c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-bold text-adm-primary text-adm-primary" data-testid="content-title">
              Content & Insights
            </h1>
            <p className="text-adm-tertiary text-adm-primary/60">Manage educational content</p>
          </div>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="copper-gradient text-[#0B0F19]" data-testid="create-content-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Content
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06] max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-adm-primary text-adm-primary">Create Content</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={createForm.title}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                      className="bg-adm-bg bg-adm-bg border-white/[0.06] border-white/[0.06]"
                      required
                    />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={createForm.content_type} onValueChange={(v) => setCreateForm(prev => ({ ...prev, content_type: v }))}>
                      <SelectTrigger className="bg-adm-bg bg-adm-bg border-white/[0.06] border-white/[0.06]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06]">
                        <SelectItem value="article">Article</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                        <SelectItem value="guide">Guide</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Input
                      value={createForm.category}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, category: e.target.value }))}
                      placeholder="e.g., nutrition, exercise, mindset"
                      className="bg-adm-bg bg-adm-bg border-white/[0.06] border-white/[0.06]"
                      required
                    />
                  </div>
                  <div>
                    <Label>Tags (comma separated)</Label>
                    <Input
                      value={createForm.tags}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, tags: e.target.value }))}
                      placeholder="e.g., parkinson, dopamine, sleep"
                      className="bg-adm-bg bg-adm-bg border-white/[0.06] border-white/[0.06]"
                    />
                  </div>
                </div>
                <div>
                  <Label>Content Body</Label>
                  <Textarea
                    value={createForm.body}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, body: e.target.value }))}
                    className="bg-adm-bg bg-adm-bg border-white/[0.06] border-white/[0.06] min-h-[200px]"
                    required
                  />
                </div>
                <Button type="submit" className="w-full copper-gradient text-[#0B0F19]" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Content'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-adm-tertiary text-adm-primary/40" />
          <Input
            placeholder="Search content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06]"
          />
        </div>

        {/* Content Grid */}
        <div className="grid gap-4">
          {filteredContent.map(item => {
            const Icon = contentTypeIcons[item.content_type] || FileText;
            return (
              <Card key={item.id} className="admin-card bg-adm-surface border-white/[0.06]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-['Outfit'] font-semibold text-adm-primary text-adm-primary">{item.title}</h3>
                        <Badge variant="outline" className="capitalize border-white/[0.06] border-white/[0.12]">
                          {item.content_type}
                        </Badge>
                        <Badge className="bg-amber-500/20 text-amber-400 capitalize">
                          {item.category}
                        </Badge>
                      </div>
                      <p className="text-adm-secondary text-adm-primary/60 line-clamp-2 mb-3">{item.body}</p>
                      {item.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {item.tags.map((tag, i) => (
                            <span key={i} className="text-xs text-adm-tertiary text-adm-primary/40 bg-slate-100 bg-adm-surface/5 px-2 py-0.5 rounded">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          
          {filteredContent.length === 0 && (
            <Card className="admin-card bg-adm-surface border-white/[0.06]">
              <CardContent className="p-8 text-center text-adm-tertiary text-adm-primary/50">
                No content found
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
